import { GuardedDeletionService } from 'projects/orchestrator/src/lib/services/guarded-deletion.service';
import { OnInit, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ListService, LocalizationPipe } from '@abp/ng.core';
import {
  CardBodyComponent,
  CardComponent,
  ConfirmationService,
  ToasterService,
} from '@abp/ng.theme.shared';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { catchError, defer, forkJoin, map, merge, Observable, of, take } from 'rxjs';
import { DistributionService, FlowTypeService } from 'cbd-proxies/planning/proxy';
import {
  StandTransformationHistoryProjectionPlanningOrchestratorService,
  StandTransformationHistoryPlanningOrchestratorService,
  type GetStandTransformationHistoryProjectionPlanningInputDto,
  type StandTransformationHistoryProjectionPlanningExcelDownloadDto,
} from 'orchestrator';
import {
  AuditUserLookupService,
  BaseCrudComponent,
  createFormModalConfigExtraLarge,
  CrudPage,
  DEFAULT_TOASTER_LIFE,
  GenericForm,
  IActionsSchema,
  ICrudService,
  ModalBuilder,
  resolveActions,
  ZnMultiSelect,
  ZnSelectQueryParams,
} from '@zendra/shared';
import {
  StandTransformationHistoryCreateDto,
  StandTransformationHistoryUpdateDto,
} from '../proxy/geographic/dtos/stand-transformation-history';
import {
  StandTransformationHistoryProjectionDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/stand-transformation-histories/models';
import { StandTransformationHistoryDetailDto } from '../proxy/geographic/dtos/stand-transformation-history-detail';
import { StandTransformationHistoryService } from '../proxy/geographic/controllers';
import {
  STANDTRANSFORMATIONHISTORY_CONFIG,
  STANDTRANSFORMATIONHISTORY_ACTION_IDS,
  STANDTRANSFORMATIONHISTORY_FIELDS,
} from './stand-transformation-history.config';
import { StandTransformationHistoryDetailAsideComponent } from './stand-transformation-history-detail-aside/stand-transformation-history-detail-aside';
import {
  StandTransformationHistoryDetailModalComponent,
  type StandTransformationHistoryDetailMode,
} from './stand-transformation-history-detail-modal/stand-transformation-history-detail-modal';
import { StandFilterLoader } from './services/stand-filter-loader';

type HistoryUserLookupResult = {
  status: 'resolved' | 'absent' | 'missingUserName' | 'error';
  id: string | null;
  user: { userName?: string | null; displayName?: string | null } | null;
  error?: unknown;
};

type StandTransformationHistoryViewRow = StandTransformationHistoryProjectionDto & {
  [key: string]: unknown;
  flowDate?: string | null;
  flowType?: { name?: string | null } | null;
  flowTypeName?: string | null;
  flowUser?: { name?: string | null } | null;
  flowUserName?: string | null;
  inventoryDistributionStatus?: { name?: string | null } | null;
  inventoryDistributionStatusName?: string | null;
  inventoryDistributionUser?: { name?: string | null } | null;
  inventoryDistributionUserName?: string | null;
  inventoryDistributionUserLookup?: HistoryUserLookupResult;
  activityDistributionStatus?: { name?: string | null } | null;
  activityDistributionStatusName?: string | null;
  activityDistributionUser?: { name?: string | null } | null;
  activityDistributionUserName?: string | null;
  activityDistributionUserLookup?: HistoryUserLookupResult;
};

type StandTransformationHistoryStandFilter =
  Pick<GetStandTransformationHistoryProjectionPlanningInputDto, 'standId'>;

type StandTransformationHistoryModalResult = {
  saved?: boolean;
};

const PENDING_DISTRIBUTION_NAME = 'Pendiente';

@Component({
  selector: 'app-stand-transformation-history',
  imports: [
    CardComponent,
    CardBodyComponent,
    CrudPage,
    GenericForm,
    FormsModule,
    LocalizationPipe,
    ZnMultiSelect,
  ],
  providers: [
    ListService,
    StandTransformationHistoryService,
    StandTransformationHistoryPlanningOrchestratorService,
    ConfirmationService,
  ],
  templateUrl: './stand-transformation-history.html',
  styleUrl: './stand-transformation-history.scss',
})
export class StandTransformationHistory extends BaseCrudComponent<
  StandTransformationHistoryProjectionDto,
  StandTransformationHistoryCreateDto,
  StandTransformationHistoryUpdateDto,
  StandTransformationHistoryProjectionPlanningExcelDownloadDto
> implements OnInit {
  private readonly guardedDeletion = inject(GuardedDeletionService);
  private readonly geographicService = inject(StandTransformationHistoryService);
  private readonly orchestratorService = inject(StandTransformationHistoryProjectionPlanningOrchestratorService);
  private readonly detailService = inject(StandTransformationHistoryPlanningOrchestratorService);
  private readonly standFilterLoader = inject(StandFilterLoader);
  private readonly modalBuilder = inject(ModalBuilder);
  private readonly flowTypeService = inject(FlowTypeService);
  private readonly distributionService = inject(DistributionService);
  private readonly auditUserLookupService = inject(AuditUserLookupService);
  private readonly toasterService = inject(ToasterService);
  private detailModalRef?: NgbModalRef;
  private detailRequestVersion = 0;

  service: ICrudService<
    StandTransformationHistoryProjectionDto,
    StandTransformationHistoryCreateDto,
    StandTransformationHistoryUpdateDto,
    StandTransformationHistoryProjectionPlanningExcelDownloadDto
  > = {
    create: input => this.geographicService.create(input),
    update: (id, input) => this.geographicService.update(id, input),
    deleteByIds: ids => this.guardedDeletion.deleteByIds('stand-transformation-histories', ids),
    getList: (query: GetStandTransformationHistoryProjectionPlanningInputDto) =>
      this.orchestratorService.getList(this.withSelectedStandFilter(query)),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input =>
      this.orchestratorService.getListAsExcelFile(this.withSelectedStandFilter(input)),
  };
  config = STANDTRANSFORMATIONHISTORY_CONFIG;
  fields = STANDTRANSFORMATIONHISTORY_FIELDS;
  actions: IActionsSchema | null = null;
  override rowActionConfig = {
    title: 'Ver detalle',
    iconClass: 'bi bi-eye-fill',
  };

  readonly selectedHistory = signal<StandTransformationHistoryViewRow | null>(null);
  readonly selectedHistoryDetails = signal<StandTransformationHistoryDetailDto[]>([]);
  readonly detailLoading = signal(false);
  readonly detailMode = signal<StandTransformationHistoryDetailMode>('view');
  readonly selectedStandIds = signal<string[]>([]);
  readonly standFilterOptionsLoader = (params?: ZnSelectQueryParams) =>
    this.standFilterLoader.load(params);

  ngOnInit(): void {
    this.actions = resolveActions(this.config.actions, {
      [STANDTRANSFORMATIONHISTORY_ACTION_IDS.editInventoryDistribution]:
        () => this.editInventoryDistribution(),
      [STANDTRANSFORMATIONHISTORY_ACTION_IDS.editActivityDistribution]:
        () => this.editActivityDistribution(),
    });
  }

  protected prepareUpdateDto(
    data: any,
    selected: StandTransformationHistoryProjectionDto,
  ): StandTransformationHistoryUpdateDto {
    return {
      ...this.prepareCreateDto(data),
      description: data.description,
      inventoryDistributionComment: data.inventoryDistributionComment,
      activityDistributionDate: data.activityDistributionDate,
      activityDistributionComment: data.activityDistributionComment,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected override prepareCreateDto(
    data: any,
  ): StandTransformationHistoryCreateDto {
    return {
      flowTypeId: this.toNumber(data.flowTypeId) as number,
      description: data.description,
      inventoryDistributionStatusId: this.toNumber(
        data.inventoryDistributionStatusId,
      ) as number,
      inventoryDistributionDate: this.normalizeInventoryDistributionDate(
        data.inventoryDistributionDate,
      ),
      inventoryDistributionUserId: this.toNullableString(
        data.inventoryDistributionUserId,
      ),
      inventoryDistributionComment: data.inventoryDistributionComment,
      activityDistributionStatusId: this.toNumber(
        data.activityDistributionStatusId,
      ) as number,
      activityDistributionDate: data.activityDistributionDate,
      activityDistributionUserId: this.toNullableString(
        data.activityDistributionUserId,
      ),
      activityDistributionComment: data.activityDistributionComment,
    };
  }

  protected getEntityName(): string {
    return 'StandTransformationHistory';
  }

  override onRowAction(row: StandTransformationHistoryProjectionDto): void {
    this.openHistoryModal(row as StandTransformationHistoryViewRow, 'view');
  }

  onSelectedStandIdsChange(value: string | string[] | null): void {
    if (Array.isArray(value)) {
      this.selectedStandIds.set(value.map(String));
    } else if (value == null || value === '') {
      this.selectedStandIds.set([]);
    } else {
      this.selectedStandIds.set([String(value)]);
    }

    this.refreshGrid();
  }

  private withSelectedStandFilter<T>(
    input: T,
  ): T & StandTransformationHistoryStandFilter {
    const standIds = this.toNumberList(this.selectedStandIds());
    const baseInput = (input ?? {}) as T & StandTransformationHistoryStandFilter;

    return standIds.length
      ? { ...baseInput, standId: standIds }
      : { ...baseInput };
  }

  private editInventoryDistribution(): void {
    const selectedHistory = this.getSelectedHistoryRow();
    if (!selectedHistory) {
      return;
    }

    this.loadHistoryDisplayData(selectedHistory)
      .pipe(take(1))
      .subscribe(history => {
        if (!this.hasPendingInventoryDistribution(history)) {
          this.toasterService.error(
            'Geographic::Entity.StandTransformationHistory.InventoryDistributionPreconditionMessage',
            '',
            { life: DEFAULT_TOASTER_LIFE },
          );
          return;
        }

        this.openHistoryModal(history, 'editInventoryDistribution', {
          skipHistoryDisplayLoad: true,
        });
      });
  }

  private editActivityDistribution(): void {
    const selectedHistory = this.getSelectedHistoryRow();
    if (!selectedHistory) {
      return;
    }

    this.loadHistoryDisplayData(selectedHistory)
      .pipe(take(1))
      .subscribe(history => {
        if (!this.hasPendingActivityDistribution(history)) {
          this.toasterService.error(
            'Geographic::Entity.StandTransformationHistory.ActivityDistributionPreconditionMessage',
            '',
            { life: DEFAULT_TOASTER_LIFE },
          );
          return;
        }

        this.openHistoryModal(history, 'editActivityDistribution', {
          skipHistoryDisplayLoad: true,
        });
      });
  }

  private openHistoryModal(
    row: StandTransformationHistoryViewRow,
    mode: StandTransformationHistoryDetailMode,
    options?: { skipHistoryDisplayLoad?: boolean },
  ): void {
    const requestVersion = ++this.detailRequestVersion;

    this.detailMode.set(mode);
    this.selectedHistory.set(row);
    this.selectedHistoryDetails.set([]);

    this.detailLoading.set(true);
    this.openDetailModal();

    forkJoin({
      details: this.loadHistoryDetails(row.id),
      history: options?.skipHistoryDisplayLoad
        ? of(row)
        : this.loadHistoryDisplayData(row),
    })
      .pipe(
        catchError(() =>
          of({
            details: [] as StandTransformationHistoryDetailDto[],
            history: row,
          }),
        ),
      )
      .subscribe(({ details, history }) => {
        if (requestVersion !== this.detailRequestVersion) {
          return;
        }

        this.selectedHistory.set(history);
        this.selectedHistoryDetails.set(details);
        this.detailLoading.set(false);
        this.refreshDetailModal();
      });
  }

  private openDetailModal(): void {
    const currentModalRef = this.detailModalRef;
    this.detailModalRef = undefined;
    currentModalRef?.dismiss();

    const modalRef = this.modalBuilder.openModal(
      StandTransformationHistoryDetailModalComponent,
      this.buildDetailModalConfig(),
    );

    this.detailModalRef = modalRef;
    merge(
      modalRef.closed.pipe(
        map(result => ({
          result: result as StandTransformationHistoryModalResult | undefined,
        })),
      ),
      modalRef.dismissed.pipe(map(() => ({ result: undefined }))),
    )
      .pipe(take(1))
      .subscribe(({ result }) => this.handleDetailModalClosed(modalRef, result));
  }

  private refreshDetailModal(): void {
    if (!this.detailModalRef) {
      return;
    }

    this.detailModalRef.componentInstance.configuration =
      this.buildDetailModalConfig();
    this.detailModalRef.componentInstance.componentInputs =
      this.buildDetailModalInputs();
  }

  private buildDetailModalConfig() {
    const isEditMode =
      this.detailMode() === 'editInventoryDistribution' ||
      this.detailMode() === 'editActivityDistribution';

    return createFormModalConfigExtraLarge({
      component: StandTransformationHistoryDetailModalComponent,
      title: 'Geographic::Entity.StandTransformationHistory.DetailModalTitle',
      subTitle:
        'Geographic::Entity.StandTransformationHistory.DetailModalSubTitle',
      subTitlePill: { label: this.formatHistoryId(this.selectedHistory()?.id) },
      dismissLabel: 'LanguageService::Close',
      confirmLabel: isEditMode ? 'LanguageService::Save' : '',
      asideConfig: {
        show: true,
        title:
          'Geographic::Entity.StandTransformationHistory.DetailModalAsideTitle',
        component: StandTransformationHistoryDetailAsideComponent,
        inputs: this.buildDetailAsideInputs(),
      },
      inputs: this.buildDetailModalInputs(),
    });
  }

  private buildDetailModalInputs() {
    return {
      history: this.selectedHistory(),
      loading: this.detailLoading(),
      mode: this.detailMode(),
    };
  }

  private buildDetailAsideInputs() {
    return {
      details: this.selectedHistoryDetails(),
      loading: this.detailLoading(),
    };
  }

  private handleDetailModalClosed(
    modalRef: NgbModalRef,
    result?: StandTransformationHistoryModalResult,
  ): void {
    if (this.detailModalRef === modalRef) {
      this.detailModalRef = undefined;
    }

    this.detailLoading.set(false);

    if (!result?.saved) {
      return;
    }

    this.toaster.success('LanguageService::Actions:SuccessMessage', '', {
      life: DEFAULT_TOASTER_LIFE,
      messageLocalizationParams: [
        this.localizationService.instant(this.getSuccessActionKey()),
      ],
    });
    this.refreshGrid();
  }

  private loadHistoryDetails(
    historyId?: number,
  ): Observable<StandTransformationHistoryDetailDto[]> {
    if (!historyId) {
      return of([]);
    }

    return this.detailService
      .getDetails({
        standTransformationHistoryId: [historyId],
        skipCount: 0,
        maxResultCount: 100,
      })
      .pipe(
        map(response => response.items ?? []),
        catchError(() => of([])),
      );
  }

  private loadHistoryDisplayData(
    row: StandTransformationHistoryViewRow,
  ): Observable<StandTransformationHistoryViewRow> {
    return forkJoin({
      flowType: this.loadOptionalLookup(row.flowTypeId, id =>
        this.flowTypeService.get(id),
      ),
      inventoryDistributionStatus: this.loadOptionalLookup(
        row.inventoryDistributionStatusId,
        id => this.distributionService.get(id),
      ),
      inventoryDistributionUser: this.loadUserLookup(
        row.inventoryDistributionUserId,
      ),
      activityDistributionStatus: this.loadOptionalLookup(
        row.activityDistributionStatusId,
        id => this.distributionService.get(id),
      ),
      activityDistributionUser: this.loadUserLookup(
        row.activityDistributionUserId,
      ),
    }).pipe(
      map(lookups => ({
        ...row,
        flowTypeName: lookups.flowType?.name ?? null,
        inventoryDistributionStatusName:
          lookups.inventoryDistributionStatus?.name ?? null,
        inventoryDistributionUserName: this.getUserDisplayName(
          lookups.inventoryDistributionUser.user,
        ),
        inventoryDistributionUserLookup: lookups.inventoryDistributionUser,
        activityDistributionStatusName:
          lookups.activityDistributionStatus?.name ?? null,
        activityDistributionUserName: this.getUserDisplayName(
          lookups.activityDistributionUser.user,
        ),
        activityDistributionUserLookup: lookups.activityDistributionUser,
      })),
      catchError(() => of(row)),
    );
  }

  private getSelectedHistoryRow(): StandTransformationHistoryViewRow | null {
    if (this.selectedRows.length !== 1) {
      return null;
    }

    const [selectedHistoryId] = this.selectedRows;
    return (
      this.dataset().find(row => row.id === selectedHistoryId) as
        | StandTransformationHistoryViewRow
        | undefined
    ) ?? null;
  }

  private hasPendingInventoryDistribution(
    history: StandTransformationHistoryViewRow,
  ): boolean {
    return (
      this.getFirstNonEmptyText(
        history.inventoryDistributionStatusName,
        history.inventoryDistributionStatus?.name,
      ) === PENDING_DISTRIBUTION_NAME
    );
  }

  private hasPendingActivityDistribution(
    history: StandTransformationHistoryViewRow,
  ): boolean {
    return (
      this.getFirstNonEmptyText(
        history.activityDistributionStatusName,
        history.activityDistributionStatus?.name,
      ) === PENDING_DISTRIBUTION_NAME
    );
  }

  private getSuccessActionKey(): string {
    return this.detailMode() === 'editActivityDistribution'
      ? 'Geographic::Action.Trigger.EditActivityDistribution'
      : 'Geographic::Action.Trigger.EditInventoryDistribution';
  }

  private getFirstNonEmptyText(
    ...values: Array<string | null | undefined>
  ): string | null {
    for (const value of values) {
      const trimmedValue = value?.trim();
      if (trimmedValue) {
        return trimmedValue;
      }
    }

    return null;
  }

  private loadUserLookup(
    id: string | null | undefined,
  ): Observable<HistoryUserLookupResult> {
    if (!id?.trim()) {
      return of({ status: 'absent', id: id ?? null, user: null });
    }

    return defer(() => this.auditUserLookupService.get(id)).pipe(
      map((user): HistoryUserLookupResult => ({
        status: user?.userName?.trim() ? 'resolved' : 'missingUserName',
        id,
        user: user ?? null,
      })),
      catchError((error: unknown) =>
        of<HistoryUserLookupResult>({ status: 'error', id, user: null, error }),
      ),
    );
  }

  private loadOptionalLookup<T, TId extends number | string>(
    id: TId | null | undefined,
    loader: (id: TId) => Observable<T>,
  ): Observable<T | null> {
    if (
      id == null ||
      (typeof id === 'number' && id <= 0) ||
      (typeof id === 'string' && !id.trim())
    ) {
      return of(null);
    }

    return loader(id).pipe(catchError(() => of(null)));
  }

  private formatHistoryId(id?: number): string {
    if (!id) {
      return '---';
    }

    return String(id).padStart(3, '0');
  }

  private getUserDisplayName(
    user: { userName?: string | null; displayName?: string | null } | null,
  ): string | null {
    return user?.displayName?.trim() || user?.userName?.trim() || null;
  }

  private normalizeInventoryDistributionDate(
    value: unknown,
  ): string | null | undefined {
    if (value == null) {
      return undefined;
    }

    if (value instanceof Date) {
      return this.formatDateValue(value);
    }

    if (typeof value !== 'string') {
      return this.toPrimitiveString(value);
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return undefined;
    }

    if (/^\d{8}$/.test(trimmedValue)) {
      return trimmedValue;
    }

    const matchedDateParts =
      /^(\d{4})[-/](\d{2})[-/](\d{2})(?:T.*)?$/.exec(trimmedValue);
    if (matchedDateParts) {
      const [, year, month, day] = matchedDateParts;
      return `${year}${month}${day}`;
    }

    const parsedDate = new Date(trimmedValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return trimmedValue;
    }

    return this.formatDateValue(parsedDate);
  }

  private toNullableString(value: unknown): string | null | undefined {
    if (value == null || value === '') {
      return undefined;
    }

    const stringValue = this.toPrimitiveString(value)?.trim();
    return stringValue || undefined;
  }

  private toNumber(value: unknown): number | undefined {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }

    if (typeof value !== 'string') {
      return undefined;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return undefined;
    }

    const parsedValue = Number(trimmedValue);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  private toNumberList(values?: readonly (string | number)[] | null): number[] {
    return (values ?? [])
      .map(value => this.toNumber(value))
      .filter((value): value is number => value !== undefined);
  }

  private toPrimitiveString(value: unknown): string | null | undefined {
    if (value == null) {
      return undefined;
    }

    if (typeof value === 'string') {
      return value;
    }

    if (
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      typeof value === 'bigint'
    ) {
      return String(value);
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString();
    }

    return undefined;
  }

  private formatDateValue(value: Date): string | undefined {
    if (Number.isNaN(value.getTime())) {
      return undefined;
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    return `${year}${month}${day}`;
  }
}
